import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { ShieldCheck, ShieldWarning, CheckCircle, X } from "@phosphor-icons/react";

export default function Settings() {
    const { user, setup2fa, enable2fa, disable2fa } = useAuth();
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");
    const [busy, setBusy] = useState(false);
    const [enroll, setEnroll] = useState(null); // {qr_data_url, secret, provisioning_uri}
    const [code, setCode] = useState("");
    const [disableMode, setDisableMode] = useState(false);
    const [disableCode, setDisableCode] = useState("");

    if (!user || typeof user !== "object") {
        return (
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 text-[#c2c2c2]">
                Loading…
            </div>
        );
    }

    const start = async () => {
        setErr("");
        setMsg("");
        setBusy(true);
        try {
            const d = await setup2fa();
            setEnroll(d);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const confirm = async () => {
        setErr("");
        setBusy(true);
        try {
            await enable2fa(code.trim());
            setMsg("Two-factor authentication is now enabled. You'll be asked for a code on every sign-in.");
            setEnroll(null);
            setCode("");
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const cancelEnroll = () => {
        setEnroll(null);
        setCode("");
        setErr("");
    };

    const onDisable = async () => {
        setErr("");
        setBusy(true);
        try {
            await disable2fa(disableCode.trim());
            setMsg("Two-factor authentication has been disabled.");
            setDisableMode(false);
            setDisableCode("");
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const enabled = !!user.twofa_enabled;

    return (
        <div className="max-w-[900px] mx-auto px-6 md:px-10 py-16" data-testid="settings-page">
            <div className="overline mb-3">Account settings</div>
            <h1 className="font-display text-4xl md:text-5xl leading-tight mb-2">
                Security &amp; sign-in
            </h1>
            <p className="text-[#c2c2c2] mb-10 max-w-2xl leading-relaxed">
                Manage how you sign in to your SmartNote2Self License Risk Scan account.
            </p>

            {msg && (
                <div className="block-opportunity mb-6 text-sm" data-testid="settings-msg">
                    <CheckCircle size={14} weight="bold" className="inline mr-1.5 -mt-0.5" />
                    {msg}
                </div>
            )}
            {err && (
                <div className="block-risk mb-6 text-sm" data-testid="settings-error">
                    {err}
                </div>
            )}

            <section className="border border-[#2a2a2a] bg-[#141414] p-6 md:p-8" data-testid="settings-2fa-section">
                <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                    <div className="flex items-start gap-3">
                        {enabled ? (
                            <ShieldCheck size={26} weight="bold" className="text-[#2ec5b6] mt-0.5" />
                        ) : (
                            <ShieldWarning size={26} weight="bold" className="text-[#d4d4d4] mt-0.5" />
                        )}
                        <div>
                            <div className="overline mb-1">Two-factor authentication</div>
                            <h2 className="font-display text-xl text-white">
                                {enabled ? "Enabled — your account is protected" : "Not enabled"}
                            </h2>
                            <p className="text-sm text-[#c2c2c2] mt-1.5 leading-relaxed max-w-xl">
                                {enabled
                                    ? "On every sign-in we'll ask for a 6-digit code from your authenticator app (Google Authenticator, 1Password, Authy, etc.)."
                                    : "Add a second layer beyond your password. You'll need an authenticator app (Google Authenticator, 1Password, Authy, etc.) on your phone."}
                            </p>
                        </div>
                    </div>
                    <div>
                        {!enabled && !enroll && (
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={start}
                                disabled={busy}
                                data-testid="settings-2fa-enable-button"
                            >
                                {busy ? "Generating…" : "Enable 2FA"}
                            </button>
                        )}
                        {enabled && !disableMode && (
                            <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => {
                                    setDisableMode(true);
                                    setErr("");
                                    setMsg("");
                                }}
                                data-testid="settings-2fa-disable-button"
                            >
                                Disable 2FA
                            </button>
                        )}
                    </div>
                </div>

                {/* ENROLLMENT FLOW */}
                {enroll && !enabled && (
                    <div className="border-t border-[#2a2a2a] pt-6 mt-6" data-testid="settings-2fa-enrollment">
                        <div className="overline mb-3">Step 1 — Scan</div>
                        <p className="text-sm text-[#c2c2c2] mb-3 max-w-xl leading-relaxed">
                            Open your authenticator app and scan this QR code. Or paste the secret manually if you can&apos;t scan.
                        </p>
                        <div className="flex flex-col md:flex-row gap-6 items-start">
                            <div className="bg-white p-3" data-testid="settings-2fa-qr-wrapper">
                                <img
                                    src={enroll.qr_data_url}
                                    alt="2FA QR code"
                                    className="block w-44 h-44"
                                    data-testid="settings-2fa-qr-image"
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="overline mb-1">Manual secret</div>
                                <div
                                    className="font-mono text-sm bg-[#0a0a0a] border border-[#2a2a2a] p-3 break-all text-[#d4d4d4]"
                                    data-testid="settings-2fa-secret"
                                >
                                    {enroll.secret}
                                </div>
                                <div className="overline mt-5 mb-2">Step 2 — Confirm</div>
                                <p className="text-sm text-[#c2c2c2] mb-3">
                                    Enter the 6-digit code your authenticator app shows.
                                </p>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={6}
                                    pattern="[0-9]{6}"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    placeholder="123456"
                                    className="input-base font-mono tracking-[0.4em] text-2xl text-center max-w-xs"
                                    data-testid="settings-2fa-confirm-code-input"
                                />
                                <div className="mt-4 flex gap-2">
                                    <button
                                        type="button"
                                        className="btn-primary"
                                        onClick={confirm}
                                        disabled={busy || code.length !== 6}
                                        data-testid="settings-2fa-confirm-button"
                                    >
                                        {busy ? "Confirming…" : "Confirm & enable"}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-ghost"
                                        onClick={cancelEnroll}
                                        disabled={busy}
                                        data-testid="settings-2fa-cancel-enrollment-button"
                                    >
                                        <X size={14} weight="bold" /> Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* DISABLE FLOW */}
                {enabled && disableMode && (
                    <div className="border-t border-[#2a2a2a] pt-6 mt-6" data-testid="settings-2fa-disable-form">
                        <div className="overline mb-2">Confirm with a code</div>
                        <p className="text-sm text-[#c2c2c2] mb-3 max-w-xl">
                            For safety, enter a fresh 6-digit code from your authenticator app to disable 2FA.
                        </p>
                        <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={disableCode}
                            onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder="123456"
                            className="input-base font-mono tracking-[0.4em] text-2xl text-center max-w-xs"
                            data-testid="settings-2fa-disable-code-input"
                        />
                        <div className="mt-3 flex gap-2">
                            <button
                                type="button"
                                className="btn-secondary"
                                onClick={onDisable}
                                disabled={busy || disableCode.length !== 6}
                                data-testid="settings-2fa-disable-confirm-button"
                            >
                                {busy ? "Disabling…" : "Disable 2FA"}
                            </button>
                            <button
                                type="button"
                                className="btn-ghost"
                                onClick={() => {
                                    setDisableMode(false);
                                    setDisableCode("");
                                }}
                                disabled={busy}
                            >
                                Keep 2FA on
                            </button>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
